import api from '@api';
import config from '@config';
import { ExportTypeType, GridRequestType, TableColumnType } from 'src/types/table';
import translate from '@translate';

// Create a loading indicator element
const createLoadingIndicator = () => {
    // Create container
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '50%';
    container.style.left = '50%';
    container.style.transform = 'translate(-50%, -50%)';
    container.style.backgroundColor = 'white';
    container.style.padding = '20px';
    container.style.borderRadius = '5px';
    container.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
    container.style.zIndex = '9999';
    container.style.display = 'flex';
    container.style.alignItems = 'center';
    container.style.justifyContent = 'center';
    container.style.flexDirection = 'column';

    // Create spinner element
    const spinner = document.createElement('div');
    spinner.style.width = '40px';
    spinner.style.height = '40px';
    spinner.style.border = '4px solid #f3f3f3';
    spinner.style.borderTop = '4px solid #4CAF50';
    spinner.style.borderRadius = '50%';
    spinner.style.animation = 'spin 1s linear infinite';

    // Add keyframes for spinner animation
    const style = document.createElement('style');
    style.textContent = `
        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
    `;
    document.head.appendChild(style);

    // Create text
    const text = document.createElement('div');
    text.textContent = translate('documents.downloading') || 'Stahování souboru...';
    text.style.marginTop = '10px';
    text.style.fontWeight = 'bold';

    // Assemble the elements
    container.appendChild(spinner);
    container.appendChild(text);

    return {
        container,
        remove: () => {
            container.remove();
            style.remove();
        }
    };
};

/**
 * Export data to a file using the server-side export functionality with streaming support
 * @param type Export type (CSV or XLSX)
 * @param filename Filename for the exported file
 * @param gridRequest Grid request parameters
 * @param endpoint API endpoint to fetch data from
 * @param translatedColumns Optional map of column keys to their translated names
 */
export const exportFile = async (
    type: ExportTypeType,
    filename: string,
    gridRequest: GridRequestType,
    endpoint: string,
    translatedColumns?: Record<string, string>
): Promise<void> => {
    try {
        // Create and show loading indicator
        const loadingIndicator = createLoadingIndicator();
        document.body.appendChild(loadingIndicator.container);
        // Prepare headers
        const headers: Record<string, string> = {
            'x-export-type': type,
            'x-export-filename': encodeURIComponent(filename)
        };

        // Create a modified grid request that includes translated columns
        const modifiedGridRequest = { ...gridRequest };

        // Add translated column names to the request if provided
        if (translatedColumns && Object.keys(translatedColumns).length > 0) {
            // Add metadata field if it doesn't exist
            if (!modifiedGridRequest.metadata) {
                modifiedGridRequest.metadata = {};
            }
            // Add translations to metadata
            modifiedGridRequest.metadata.translatedColumns = translatedColumns;
        }

        const response = await api('post', endpoint, modifiedGridRequest, {
            responseType: 'blob',
            headers
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${filename}.${type.toLowerCase()}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        // Remove loading indicator after a short delay
        setTimeout(() => {
            loadingIndicator.remove();
        }, 1000);
    } catch (error) {
        console.error(`Error exporting ${type} file:`, error);
        throw error;
    }
};

export default exportFile;
